<script>
import ControlBar from '@/components/layouts/dashboard/ControlBar';
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import EmptyState from '@/components/ui/EmptyState';
import SiteCard from '@/components/sites/SiteCard';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import debounce from 'lodash/debounce';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'Sites',
  components: {
    ControlBar,
    DashboardLayout,
    EmptyState,
    SiteCard,
    SidebarLink,
    SidebarLinkGroup
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('Sites'),
      breadcrumbs: [{ text: this.$t('Sites') }],
      canLoadMore: false,
      favorites: [],
      hasSites: true,
      isLoadingSites: false,
      limit: 50,
      numSites: 0,
      offset: 0,
      search: '',
      sites: [],
      statusCode: null
    };
  },
  created() {
    // Fetch initial data
    Promise.all([
      this.fetchSites('', this.limit, 0).then(res => (this.hasSites = res.data.count > 0)),
      this.fetchFavorites()
    ])
      .then(() => {
        this.statusCode = 200;
        this.$nextTick(() => this.$refs.search.focus());
      })
      .catch(err => (this.statusCode = err.response.status));
  },
  mounted() {
    // Watch for infinite scrolling
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeRouteLeave(to, from, next) {
    // Stop watching for infinite scrolling
    window.removeEventListener('scroll', this.handleScroll);
    next();
  },
  methods: {
    // Fetches a list of favorite sites
    fetchFavorites() {
      const params = { sortBy: 'name' };
      return this.$http.get(`/users/${this.$store.state.user.id}/favorites`, { params }).then(res => {
        res.data.favorites.map(fav => this.favorites.push(fav.site));
        return res;
      });
    },

    // Fetches a list of sites
    fetchSites(search, limit, offset) {
      this.isLoadingSites = true;
      return this.$http
        .get('/sites', {
          params: {
            search,
            limit,
            offset,
            sortBy: 'name'
          }
        })
        .then(res => {
          this.canLoadMore = res.data.count > res.data.offset + res.data.limit;
          this.offset = res.data.offset;

          if (res.data.offset === 0) {
            this.sites = res.data.sites;
            this.numSites = res.data.count;
          } else {
            res.data.sites.map(site => this.sites.push(site));
          }

          return res;
        })
        .finally(() => (this.isLoadingSites = false));
    },

    // Deletes a site
    deleteSite(site) {
      const message = this.$t('Are you sure you want to do this?');
      this.$confirm(message, {
        title: this.$t('Delete'),
        confirmButtonText: this.$t('Delete'),
        confirmButtonClass: 'el-button--danger',
        cancelButtonText: this.$t('Cancel'),
        callback: action => {
          if (action === 'confirm') {
            this.sites = this.sites.filter(s => s.id !== site.id);
            this.$http.delete(`/sites/${site.id}`);
          }
        }
      });
    },

    // Refreshes the list as the user types a search
    handleSearch: debounce(function() {
      this.fetchSites(this.search, this.limit, 0).catch(err => {
        this.$notify({
          type: 'error',
          title: this.$t('Error'),
          message: getErrorMessage(err.response.data.code)
        });
      });
    }, 250),

    // Loads more when scrolling reaches the bottom
    handleScroll: debounce(
      function() {
        const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
        const scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight;
        const windowHeight = window.innerHeight;
        const threshold = windowHeight * 4;

        if (scrollTop > scrollHeight - threshold) {
          this.loadMore();
        }
      },
      500,
      { maxWait: 100 }
    ),

    // Loads the next batch of sites
    loadMore() {
      if (this.canLoadMore) {
        this.fetchSites(this.search, this.limit, this.offset + this.limit).catch(err => {
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        });
      }
    },

    // Updates a site's screenshot
    updateScreenshot(siteId, screenshot) {
      this.sites = this.sites.map(site => {
        return site.id === siteId ? Object.assign(site, { screenshot }) : site;
      });
    }
  }
};
</script>

<template>
  <dashboard-layout :status-code="statusCode" size="large">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Admin -->
      <sidebar-link-group v-if="$store.state.user.canManageSites" :title="$t('Admin')">
        <sidebar-link :text="$t('New Site')" :to="{ name: 'NewSite' }" icon="el-icon-fa-plus" />
      </sidebar-link-group>

      <!-- Favorites -->
      <sidebar-link-group v-if="favorites.length" :title="$t('Favorites')">
        <sidebar-link
          v-for="favorite in favorites"
          :key="favorite.id"
          :text="favorite.name"
          :to="{ name: 'Site', params: { siteId: favorite.id }}"
          icon="el-icon-fa-heart"
        />
      </sidebar-link-group>
    </template>

    <!-- Control bar -->
    <control-bar :collapsable="false">
      <template slot="primary">
        <el-input
          ref="search"
          v-model="search"
          :placeholder="$t('Search Sites')"
          :disabled="!hasSites"
          :prefix-icon="isLoadingSites ? 'el-icon-fa-circle-notch el-icon-fa-spin' : 'el-icon-fa-search'"
          clearable
          @input="handleSearch"
        />
      </template>
      <template slot="secondary">
        <el-button v-if="$store.state.user.canManageSites" type="primary" @click="$router.push({ name: 'NewSite' })">
          {{ $t('New Site') }}
        </el-button>
      </template>
    </control-bar>

    <!-- Site cards -->
    <div v-if="hasSites" class="site-cards">
      <el-row :gutter="32">
        <site-card
          v-for="site in sites"
          :key="site.id"
          :site="site"
          @delete="deleteSite(site)"
          @screenshot-ready="updateScreenshot(site.id, $event)"
        />
      </el-row>
    </div>

    <!-- Load more button -->
    <div v-if="canLoadMore" class="text-center">
      <el-button :loading="isLoadingSites" @click="loadMore">
        <i class="el-icon-fa-arrow-down" />
      </el-button>
    </div>

    <!-- No results -->
    <empty-state
      v-if="search.length && !sites.length"
      :title="$t('No Results')"
      class="empty-state"
    />

    <!-- No sites -->
    <empty-state
      v-if="!hasSites"
      :title="$t('No Sites')"
      class="empty-state"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

// No styles yet
</style>
